import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { CustomLoading } from "components/CustomLoading";
import { Lock, Users, MoreHorizontal, Share2 } from "lucide-react";
import { useBookmarks } from "./useBookmarks";
import CategoryTabs, { CategoryTabKey, TabCounts } from "./CategoryTabs";
import NewCategoryMenu, { NewMenuAction } from "./NewCategoryMenu";
import QuestionForm from "./QuestionForm";
import LinkForm from "./LinkForm";
import DocumentForm from "./DocumentForm";
import PageRequestForm from "./PageRequestForm";
import DocumentRenameForm from "./DocumentRenameForm";
import DocumentMoveForm from "./DocumentMoveForm";
import ConfirmModal from "./ConfirmModal";
import ShareModal from "./ShareModal";
import CategoryVisibilityModal from "./CategoryVisibilityModal";
import LimitedAccessBanner from "./LimitedAccessBanner";
import Breadcrumb, { BreadcrumbPill } from "./Breadcrumb";
import { User, loadUsers } from "./user-display";
import { RowKind, RowKebab } from "./category-rows";
import { CatTableBody, Row, withPinnedFirst } from "./CatTableBody";
import { reorderPinned } from "./dnd-helpers";

interface Category {
    id: string;
    name: string;
    description: string | null;
    color: string | null;
    icon: string | null;
    sortMode: string;
    accessLevel: string;
    allowedRoles: string | null;
    ownerUserIds?: string[];
}

interface Page {
    id: string;
    title: string;
    status: string;
    updatedAt: string;
    isPinned: boolean;
    folderId: string | null;
    createdBy: string | null;
    ownerId: string | null;
}

interface Folder {
    id: string;
    name: string;
    description: string | null;
    parentFolderId: string | null;
    icon: string | null;
    color: string | null;
    sortMode: string;
    isPinned: boolean;
    sortOrder: number;
    updatedAt: string;
    createdBy: string | null;
}

interface Question {
    id: string;
    title: string;
    body: string | null;
    categoryId: string | null;
    createdAt: string;
    updatedAt: string;
    createdBy: string | null;
    authorId: string | null;
}

interface LinkItem {
    id: string;
    title: string;
    url: string;
    description: string | null;
    categoryId: string;
    folderId: string | null;
    isPinned: boolean;
    createdAt: string;
    updatedAt: string;
    createdBy: string | null;
}

interface DocumentItem {
    id: string;
    filename: string;
    categoryId: string;
    folderId: string | null;
    isPinned: boolean;
    sortOrder: number;
    createdAt: string;
    updatedAt: string;
    createdBy: string | null;
}

interface Props {
    categoryId: string;
    categories: Category[];
    isAdmin: boolean;
    canManage: boolean;
    currentUserId: string | null;
    onSelectPage?: (pageId: string) => void;
    onSelectFolder?: (folderId: string) => void;
    onSelectQuestion?: (questionId: string) => void;
    onCreatePage?: () => void;
    onCreateFolder?: () => void;
    onEditCategory?: (cat: Category) => void;
    onDeleteCategory?: (cat: Category) => void;
    onArchiveCategory?: (cat: Category) => void;
    onCategoryChanged?: () => void;
    treeVersion?: number;
}

// Row shape and rendering are shared with FolderView — see CatTableBody.

export default function CategoryContentView({
    categoryId,
    categories,
    isAdmin,
    canManage,
    currentUserId,
    onSelectPage,
    onSelectFolder,
    onSelectQuestion,
    onCreatePage,
    onCreateFolder,
    onEditCategory,
    onDeleteCategory,
    onArchiveCategory,
    onCategoryChanged,
    treeVersion,
}: Props) {
    const [pages, setPages] = useState<Page[]>([]);
    const [folders, setFolders] = useState<Folder[]>([]);
    const [questions, setQuestions] = useState<Question[]>([]);
    const [links, setLinks] = useState<LinkItem[]>([]);
    const [documents, setDocuments] = useState<DocumentItem[]>([]);
    const [users, setUsers] = useState<Map<string, User>>(new Map());
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<CategoryTabKey>("everything");
    const [showQuestion, setShowQuestion] = useState(false);
    const [showLink, setShowLink] = useState(false);
    const [showDocument, setShowDocument] = useState(false);
    const [showPageRequest, setShowPageRequest] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);
    const [kebabOpen, setKebabOpen] = useState(false);
    const [shareOpen, setShareOpen] = useState(false);
    const [visibilityOpen, setVisibilityOpen] = useState(false);
    const kebabRef = useRef<HTMLDivElement>(null);
    const [editLink, setEditLink] = useState<LinkItem | null>(null);
    const [deleteLink, setDeleteLink] = useState<LinkItem | null>(null);
    const [renameDoc, setRenameDoc] = useState<DocumentItem | null>(null);
    const [moveDoc, setMoveDoc] = useState<DocumentItem | null>(null);
    const [deleteDoc, setDeleteDoc] = useState<DocumentItem | null>(null);
    const [openKebabId, setOpenKebabId] = useState<string | null>(null);
    const bookmarks = useBookmarks();

    const category = categories.find(c => c.id === categoryId);

    useEffect(() => {
        loadUsers().then(us => {
            const m = new Map<string, User>();
            us.forEach(u => m.set(u.id, u));
            setUsers(m);
        }).catch(() => {});
    }, []);

    useEffect(() => {
        if (!kebabOpen) return;
        const handler = (e: MouseEvent) => {
            if (!kebabRef.current?.contains(e.target as Node)) setKebabOpen(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [kebabOpen]);

    useEffect(() => {
        setLoading(true);
        Promise.all([
            axios.get(`/api/manuals/categories/${categoryId}/pages`),
            axios.get(`/api/manuals/categories/${categoryId}/folders`),
            axios.get(`/api/manuals/categories/${categoryId}/documents`),
            axios.get(`/api/manuals/categories/${categoryId}/links`),
            axios.get(`/api/manuals/questions`, { params: { categoryId } }),
        ])
            .then(([pagesRes, foldersRes, docsRes, linksRes, qRes]) => {
                if (pagesRes.data.success) setPages(pagesRes.data.items);
                if (foldersRes.data.success) setFolders(foldersRes.data.items);
                if (docsRes.data.success) setDocuments(docsRes.data.items);
                if (linksRes.data.success) setLinks(linksRes.data.items);
                if (qRes.data.success) setQuestions(qRes.data.items);
                setLoading(false);
            })
            .catch(() => {
                toast.error("Failed to load category contents");
                setLoading(false);
            });
    }, [categoryId, treeVersion, reloadKey]);

    const rootFolders = folders;
    const rootPages = useMemo(() => pages.filter(p => !p.folderId), [pages]);
    const rootLinks = useMemo(() => links.filter(l => !l.folderId), [links]);
    const rootDocs = useMemo(() => documents.filter(d => !d.folderId), [documents]);
    const catQuestions = useMemo(() => questions.filter(q => q.categoryId === categoryId), [questions, categoryId]);

    const counts: TabCounts = {
        subfolders: rootFolders.length,
        pages: rootPages.length,
        questions: catQuestions.length,
        links: rootLinks.length,
        documents: rootDocs.length,
        requests: 0,
    };

    function handleNew(action: NewMenuAction) {
        switch (action) {
            case "question": setShowQuestion(true); break;
            case "page": onCreatePage?.(); break;
            case "link": setShowLink(true); break;
            case "document": setShowDocument(true); break;
            case "import": toast("Page import coming soon"); break;
            case "subfolder": onCreateFolder?.(); break;
            case "request": setShowPageRequest(true); break;
        }
    }

    function bumpReload() { setReloadKey(k => k + 1); }

    if (loading) return <div className="m-loading"><CustomLoading /></div>;

    const folderRows: Row[] = withPinnedFirst(rootFolders.map(f => ({
        kind: "folder",
        id: f.id,
        title: f.name,
        modifiedAt: f.updatedAt,
        ownerId: f.createdBy,
        onClick: () => onSelectFolder?.(f.id),
        isPinned: f.isPinned,
        iconName: f.icon,
        iconColor: f.color,
    })));
    const pageRows: Row[] = withPinnedFirst(rootPages.map(p => ({
        kind: "page",
        id: p.id,
        title: p.title,
        modifiedAt: p.updatedAt,
        ownerId: p.createdBy,
        onClick: () => onSelectPage?.(p.id),
        isPinned: p.isPinned,
    })));
    const questionRows: Row[] = catQuestions.map(q => ({
        kind: "question",
        id: q.id,
        title: q.title,
        modifiedAt: q.updatedAt || q.createdAt,
        ownerId: q.createdBy || q.authorId,
        onClick: () => onSelectQuestion?.(q.id),
    }));
    const linkRows: Row[] = withPinnedFirst(rootLinks.map(l => {
        return {
            kind: "link",
            id: l.id,
            title: l.title,
            modifiedAt: l.updatedAt || l.createdAt,
            ownerId: l.createdBy,
            onClick: () => window.open(l.url, "_blank"),
            isPinned: l.isPinned,
            subtext: l.url,
            description: l.description,
            rowKebab: canManage ? (
                <RowKebab
                    isOpen={openKebabId === l.id}
                    onToggle={() => setOpenKebabId(id => id === l.id ? null : l.id)}
                    items={[
                        { label: "Edit", onClick: () => { setEditLink(l); setOpenKebabId(null); } },
                        ...(isAdmin ? [{ label: "Delete", onClick: () => { setDeleteLink(l); setOpenKebabId(null); }, danger: true }] : []),
                    ]}
                />
            ) : undefined,
        };
    }));
    const documentRows: Row[] = withPinnedFirst(rootDocs.map(d => {
        return {
            kind: "document",
            id: d.id,
            title: d.filename,
            modifiedAt: d.updatedAt || d.createdAt,
            ownerId: d.createdBy,
            onClick: () => window.location.assign(`/api/manuals/documents/${d.id}/download`),
            isPinned: d.isPinned,
            rowKebab: canManage ? (
                <RowKebab
                    isOpen={openKebabId === d.id}
                    onToggle={() => setOpenKebabId(id => id === d.id ? null : d.id)}
                    items={[
                        { label: "Rename", onClick: () => { setRenameDoc(d); setOpenKebabId(null); } },
                        { label: "Move", onClick: () => { setMoveDoc(d); setOpenKebabId(null); } },
                        ...(isAdmin ? [{ label: "Delete", onClick: () => { setDeleteDoc(d); setOpenKebabId(null); }, danger: true }] : []),
                    ]}
                />
            ) : undefined,
        };
    }));

    // Reorder locally so the row lands where it was dropped; a rejected save reloads
    // the category rather than leaving a phantom order on screen.
    async function handleReorder(kind: RowKind, orderedIds: string[]) {
        const rank = new Map(orderedIds.map((id, i) => [id, i]));
        const sortPinned = <T extends { id: string; isPinned: boolean }>(items: T[]): T[] => {
            const pinned = items.filter(i => i.isPinned && rank.has(i.id))
                .sort((a, b) => (rank.get(a.id) ?? 0) - (rank.get(b.id) ?? 0));
            return [...pinned, ...items.filter(i => !(i.isPinned && rank.has(i.id)))];
        };

        switch (kind) {
            case "folder": setFolders(sortPinned); break;
            case "page": setPages(sortPinned); break;
            case "link": setLinks(sortPinned); break;
            case "document": setDocuments(sortPinned); break;
            default: return;
        }

        if (!(await reorderPinned(kind, orderedIds))) bumpReload();
    }

    let visibleRows: Row[] = [];
    switch (activeTab) {
        case "everything":
            visibleRows = [...folderRows, ...pageRows, ...questionRows, ...linkRows, ...documentRows];
            break;
        case "subfolders": visibleRows = folderRows; break;
        case "pages": visibleRows = pageRows; break;
        case "questions": visibleRows = questionRows; break;
        case "links": visibleRows = linkRows; break;
        case "documents": visibleRows = documentRows; break;
        case "requests": visibleRows = []; break;
    }

    const isRestricted = category?.accessLevel === "restricted";
    // Admins manage any category; a non-admin Editor only one they own.
    const canManageCategory =
        isAdmin || (canManage && !!currentUserId && (category?.ownerUserIds ?? []).includes(currentUserId));

    const pills: BreadcrumbPill[] = [{
        kind: "category",
        label: category?.name || "Category",
        iconName: category?.icon || null,
        iconColor: category?.color || null,
    }];

    const headerRight = (
        <>
            {canManageCategory ? (
                <button
                    className="m-cat-strip-access"
                    title="Change who can access this category"
                    onClick={() => setVisibilityOpen(true)}
                >
                    {isRestricted ? <Lock size={13} /> : <Users size={13} />}
                    {isRestricted ? "Invite only" : "Everyone"}
                </button>
            ) : (
                <span className="m-cat-strip-access">
                    {isRestricted ? <Lock size={13} /> : <Users size={13} />}
                    {isRestricted ? "Invite only" : "Everyone"}
                </span>
            )}
            <button className="m-topbar-btn" onClick={() => setShareOpen(true)}>
                <Share2 size={14} /> Share
            </button>
            <div ref={kebabRef} style={{ position: "relative" }}>
                <button
                    className="m-cat-strip-kebab"
                    title="More actions"
                    onClick={e => { e.stopPropagation(); setKebabOpen(o => !o); }}
                >
                    <MoreHorizontal size={16} />
                </button>
                {kebabOpen && (
                    <div className="m-cat-kebab-menu">
                        <button
                            onClick={() => {
                                setKebabOpen(false);
                                bookmarks.toggle("category", categoryId);
                            }}
                        >
                            {bookmarks.keys.has(`category:${categoryId}`)
                                ? "Remove bookmark"
                                : "Bookmark"}
                        </button>
                        {canManageCategory && (
                            <button
                                onClick={() => {
                                    setKebabOpen(false);
                                    if (category && onEditCategory) onEditCategory(category);
                                }}
                            >
                                Edit category
                            </button>
                        )}
                        {isAdmin && (
                            <button
                                onClick={() => {
                                    setKebabOpen(false);
                                    if (category && onArchiveCategory) onArchiveCategory(category);
                                }}
                            >
                                Archive
                            </button>
                        )}
                        {isAdmin && (
                            <button
                                className="m-cat-kebab-danger"
                                onClick={() => {
                                    setKebabOpen(false);
                                    if (category && onDeleteCategory) onDeleteCategory(category);
                                }}
                            >
                                Delete
                            </button>
                        )}
                    </div>
                )}
            </div>
        </>
    );

    return (
        <div className="m-cat-page">
            <Breadcrumb pills={pills} right={headerRight} />

            <LimitedAccessBanner categoryId={categoryId} accessLevel={category?.accessLevel} refreshKey={reloadKey} />

            {category?.description && (
                <p className="m-cat-desc">{category.description}</p>
            )}

            <div className="m-cat-tabs-row">
                <CategoryTabs active={activeTab} counts={counts} onChange={setActiveTab} />
                {canManage && <NewCategoryMenu onSelect={handleNew} />}
            </div>

            <div className={`m-cat-table${activeTab === "links" ? " m-cat-table-links" : ""}`}>
                <div className="m-cat-table-head">
                    <div>Name</div>
                    {activeTab === "links" && <div>Description</div>}
                    <div>Last modified</div>
                    <div>Owner</div>
                </div>
                {visibleRows.length === 0 ? (
                    <div className="m-cat-empty-row">{emptyMessage(activeTab)}</div>
                ) : (
                    <CatTableBody
                        rows={visibleRows}
                        showDescription={activeTab === "links"}
                        users={users}
                        canReorder={isAdmin}
                        onReorder={handleReorder}
                    />
                )}
            </div>

            {showQuestion && category && (
                <QuestionForm
                    categoryId={categoryId}
                    categoryName={category.name}
                    onSave={() => { setShowQuestion(false); bumpReload(); }}
                    onCancel={() => setShowQuestion(false)}
                />
            )}
            {showLink && category && (
                <LinkForm
                    categoryId={categoryId}
                    categoryName={category.name}
                    onSave={() => { setShowLink(false); bumpReload(); }}
                    onCancel={() => setShowLink(false)}
                />
            )}
            {showDocument && category && (
                <DocumentForm
                    categoryId={categoryId}
                    categoryName={category.name}
                    onSave={() => { setShowDocument(false); bumpReload(); }}
                    onCancel={() => setShowDocument(false)}
                />
            )}
            {showPageRequest && category && (
                <PageRequestForm
                    categoryId={categoryId}
                    categoryName={category.name}
                    onSave={() => { setShowPageRequest(false); bumpReload(); }}
                    onCancel={() => setShowPageRequest(false)}
                />
            )}
            {editLink && category && (
                <LinkForm
                    categoryId={categoryId}
                    categoryName={category.name}
                    linkId={editLink.id}
                    initialTitle={editLink.title}
                    initialUrl={editLink.url}
                    initialDescription={editLink.description ?? ""}
                    onSave={() => { setEditLink(null); bumpReload(); }}
                    onCancel={() => setEditLink(null)}
                />
            )}
            {deleteLink && (
                <ConfirmModal
                    title="Delete link"
                    message={`Delete "${deleteLink.title}"? This cannot be undone.`}
                    confirmLabel="Delete"
                    danger
                    onConfirm={() => {
                        axios.delete(`/api/manuals/links/${deleteLink.id}`)
                            .then(res => {
                                if (res.data.success) {
                                    toast.success("Link deleted");
                                    setDeleteLink(null);
                                    bumpReload();
                                } else {
                                    toast.error(res.data.message || "Failed to delete");
                                }
                            })
                            .catch(err => toast.error(err.response?.data?.message || "Failed to delete"));
                    }}
                    onCancel={() => setDeleteLink(null)}
                />
            )}
            {renameDoc && (
                <DocumentRenameForm
                    doc={renameDoc}
                    onSave={() => { setRenameDoc(null); bumpReload(); }}
                    onCancel={() => setRenameDoc(null)}
                />
            )}
            {moveDoc && (
                <DocumentMoveForm
                    doc={moveDoc}
                    categories={categories}
                    onSave={() => { setMoveDoc(null); bumpReload(); }}
                    onCancel={() => setMoveDoc(null)}
                />
            )}
            {deleteDoc && (
                <ConfirmModal
                    title="Delete document"
                    message={`Delete "${deleteDoc.filename}"? This cannot be undone.`}
                    confirmLabel="Delete"
                    danger
                    onConfirm={() => {
                        axios.delete(`/api/manuals/documents/${deleteDoc.id}`)
                            .then(res => {
                                if (res.data.success) {
                                    toast.success("Document deleted");
                                    setDeleteDoc(null);
                                    bumpReload();
                                } else {
                                    toast.error(res.data.message || "Failed to delete");
                                }
                            })
                            .catch(err => toast.error(err.response?.data?.message || "Failed to delete"));
                    }}
                    onCancel={() => setDeleteDoc(null)}
                />
            )}

            {visibilityOpen && category && (
                <CategoryVisibilityModal
                    categoryId={category.id}
                    onSave={() => {
                        setVisibilityOpen(false);
                        setReloadKey(k => k + 1);
                        if (onCategoryChanged) onCategoryChanged();
                    }}
                    onCancel={() => setVisibilityOpen(false)}
                />
            )}

            {shareOpen && category && (
                <ShareModal
                    kind="category"
                    entityId={category.id}
                    label={category.name}
                    url={window.location.href}
                    iconName={category.icon}
                    iconColor={category.color}
                    isRestricted={isRestricted}
                    onClose={() => setShareOpen(false)}
                />
            )}
        </div>
    );
}

function emptyMessage(tab: CategoryTabKey): string {
    switch (tab) {
        case "everything": return "No items yet";
        case "subfolders": return "No subfolders yet";
        case "pages": return "No pages yet";
        case "questions": return "No questions yet";
        case "links": return "No links yet";
        case "documents": return "No documents yet";
        case "requests": return "No requests yet";
    }
}
